/**
 * AwbTracker — for AIR shipments, takes a MAWB number and offers:
 *   • One-click jump to the airline's official tracking page
 *   • Share the tracking link via WhatsApp / Email / Copy
 *
 * Backend resolves the IATA prefix (3 digits) → airline + url via
 * GET /api/awb-tracking/<mawb>. Falls back to a Google search link
 * when prefix is unknown.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plane, ExternalLink, Mail, MessageSquare, Copy } from "lucide-react";
import { Button } from "./ui/button";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function AwbTracker({ mawb, pfNumber, token, contactEmail, contactPhone }) {
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!mawb) { setInfo(null); return; }
    (async () => {
      setLoading(true);
      try {
        const r = await axios.get(
          `${API_URL}/awb-tracking/${encodeURIComponent(mawb)}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        setInfo(r.data);
      } catch {
        setInfo(null);
      } finally {
        setLoading(false);
      }
    })();
  }, [mawb, token]);

  if (!mawb) return null;

  const shareText = info
    ? `📦 Air shipment ${pfNumber || ""}\nAWB: ${info.mawb_full}\nAirline: ${info.airline || "—"}\nTrack: ${info.tracking_url}`
    : `AWB ${mawb} for ${pfNumber}`;

  const copy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Copied to clipboard");
    } catch { toast.error("Couldn't copy"); }
  };

  return (
    <div className="bg-white rounded-xl p-4 border shadow-sm" data-testid="awb-tracker-block">
      <div className="flex items-center justify-between mb-2">
        <h3 className="font-semibold flex items-center gap-2">
          <Plane className="w-4 h-4 text-sky-600" />
          AWB Tracking
        </h3>
        {info?.found && (
          <span className="text-[10px] uppercase tracking-wider text-sky-700 bg-sky-50 border border-sky-200 rounded-full px-2 py-0.5">
            {info.iata_code} · prefix {info.prefix}
          </span>
        )}
      </div>

      <div className="text-xs text-slate-500 mb-1">AWB number</div>
      <div className="font-mono text-sm mb-3">{info?.mawb_full || mawb}</div>

      {loading ? (
        <p className="text-xs text-slate-400">Resolving airline…</p>
      ) : info?.found ? (
        <>
          <div className="text-xs text-slate-500 mb-1">Airline</div>
          <div className="text-sm font-medium mb-3">{info.airline}</div>

          <a
            href={info.tracking_url}
            target="_blank"
            rel="noreferrer"
            className="block w-full text-center bg-sky-600 hover:bg-sky-700 text-white text-sm font-medium py-2.5 rounded-lg flex items-center justify-center gap-1.5 mb-2"
            data-testid="awb-track-btn"
          >
            <ExternalLink className="w-4 h-4" />
            Open {info.iata_code} tracking page
          </a>
        </>
      ) : (
        <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2 mb-2">
          {info?.reason || "Airline prefix not recognised — using a Google fallback."}
          {info?.tracking_url && (
            <a
              href={info.tracking_url}
              target="_blank"
              rel="noreferrer"
              className="block mt-1 text-sky-700 underline"
              data-testid="awb-track-fallback"
            >
              Search Google for this AWB →
            </a>
          )}
        </div>
      )}

      {/* Share row */}
      <div className="grid grid-cols-3 gap-2 mt-2">
        <a
          href={`https://wa.me/${(contactPhone || "").replace(/\D/g, "")}?text=${encodeURIComponent(shareText)}`}
          target="_blank"
          rel="noreferrer"
          className="text-xs text-center py-2 border rounded-lg hover:bg-slate-50 flex flex-col items-center gap-1"
          data-testid="awb-share-whatsapp"
        >
          <MessageSquare className="w-4 h-4 text-green-600" />
          WhatsApp
        </a>
        <a
          href={`mailto:${contactEmail || ""}?subject=${encodeURIComponent(
            `AWB ${info?.mawb_full || mawb} — ${pfNumber || ""}`
          )}&body=${encodeURIComponent(shareText)}`}
          className="text-xs text-center py-2 border rounded-lg hover:bg-slate-50 flex flex-col items-center gap-1"
          data-testid="awb-share-email"
        >
          <Mail className="w-4 h-4 text-emerald-600" />
          Email
        </a>
        <button
          onClick={() => copy(shareText)}
          className="text-xs py-2 border rounded-lg hover:bg-slate-50 flex flex-col items-center gap-1"
          data-testid="awb-share-copy"
        >
          <Copy className="w-4 h-4 text-slate-600" />
          Copy
        </button>
      </div>
    </div>
  );
}

export default AwbTracker;
